import {
  DialogDrawer,
  DialogDrawerContent,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import {
  pricingPlansHighlightedPlanAtom,
  pricingPlansModalOpenAtom,
} from '@/features/settings/billing/billingAtoms';
import { PracticeSeatUpgradePlans } from '@/features/settings/billing/components/pricing-plans-modal/practice-seat-upgrade-plans';
import { pricingPlansModalMessages } from '@/features/settings/billing/components/pricing-plans-modal/pricing-plans-modal.messages';
import { PricingPlansStatusBanner } from '@/features/settings/billing/components/pricing-plans-modal/pricing-plans-status-banner';
import { PricingPlanCards } from '@/features/settings/billing/components/pricing-plans-modal/PricingPlanCards';
import { usePricingPlansModalState } from '@/features/settings/billing/components/pricing-plans-modal/usePricingPlansModalState';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { TypographyH2 } from '@heidi/design-system/typography';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

export const PricingPlansModal = () => {
  const [isOpen, setIsOpen] = useAtom(pricingPlansModalOpenAtom);
  const setHighlightedPlan = useSetAtom(pricingPlansHighlightedPlanAtom);
  const wasOpenRef = useRef(false);
  const handleOpenChange = useCallback((open: boolean) => setIsOpen(open), [setIsOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) setHighlightedPlan(null);
    wasOpenRef.current = isOpen;
  }, [isOpen, setHighlightedPlan]);

  useEffect(() => {
    if (isOpen) {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.PricingPlansModal },
      });
    }
  }, [isOpen]);

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleOpenChange}>
      {/*
        880 wide with 48/32 padding is the design frame. The height stays
        content-driven — pinning it to the design's 703 would depend on every
        design-system control matching its Figma instance to the pixel, and the
        failure mode is clipped content. `PricingPlanCards` freezes the card row
        at its measured height instead, so the dialog never grows on expand.
        A single-card body (Practice seat checkout, Scribe Team upgrade) narrows
        it to the 540 design frame through `:has`, since the plan state is only
        read once the content mounts.
      */}
      <DialogDrawerContent className="flex max-h-[90dvh] min-h-0 w-full flex-col overflow-hidden bg-surface-elevated px-8 pb-8 pt-12 sm:max-w-[880px] sm:has-[[data-single-plan]]:max-w-[540px]">
        <PricingPlansModalBody />
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

function PricingPlansModalBody() {
  const modalState = usePricingPlansModalState();
  const {
    isPracticeSeatCheckoutOffered,
    isScribeTeamUpgradeOffered,
    currentPlan,
    currentSeatType,
  } = modalState;

  // Both team upgrades share the one Practice card into the seat checkout:
  // Practice Standard picks Max seats for members, Scribe Team moves the team.
  const seatCheckoutFromPlan = isPracticeSeatCheckoutOffered
    ? 'practice'
    : isScribeTeamUpgradeOffered
      ? 'scribeTeam'
      : null;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto overflow-x-hidden pb-10 sm:pb-0"
      data-single-plan={seatCheckoutFromPlan !== null || undefined}
    >
      <TypographyH2 className="text-center text-text-primary">
        <FormattedMessage
          {...(seatCheckoutFromPlan !== null
            ? pricingPlansModalMessages.teamUpgradeTitle
            : pricingPlansModalMessages.title)}
        />
      </TypographyH2>

      {seatCheckoutFromPlan !== null ? (
        <div className="flex flex-col gap-4">
          <PricingPlansStatusBanner />
          <PracticeSeatUpgradePlans
            currentPlan={currentPlan}
            currentSeatType={currentSeatType}
            fromPlan={seatCheckoutFromPlan}
          />
        </div>
      ) : (
        <>
          <PricingPlansStatusBanner />
          <PricingPlanCards modalState={modalState} />
        </>
      )}
    </div>
  );
}
